import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslations } from 'next-intl'
import { cn, formatFileSize } from '@/lib/utils'
import UploadIcon from '@/assets/icons/upload.svg'
import FileDocumentIcon from '@/assets/icons/file_document.svg'
import FolderIcon from '@/assets/icons/folder.svg'
import ReportProblemIcon from '@/assets/icons/report_problem.svg'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FileUploadItem, type FileUploadItemProps } from '@/components/ui/file-item'
import { Radio, RadioGroup } from '@/components/ui/radio'
import { CompanyFilesPickerDialog } from '@/components/dialog/CompanyFilesPickerDialog'
import { useForm, useStore } from '@tanstack/react-form'
import { z } from 'zod'
import { Field, FieldGroup } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { UserMultiSelect, type UserMultiSelectItem } from '@/components/input/UserMultiSelect'
import { SimpleSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { useCompanyAgents, useCompanyModels, useDatamanagementItems, useDepartments, useUsers } from '@/hooks/useApi'
import { getErrorMessage } from '@/lib/form'

// ---------- Types ----------

export type RoomFormValues = {
  name: string
  description: string
  customPrompt: string
  modelVersionId: string
  agentId: string
  departmentId: string
  allowAnyUsers: boolean
  isAnswerInternalDataOnly: boolean
  useCompanyFiles: boolean
  memberIds: string[]
  uploadDocuments: Array<{ name: string; file: File; fileType: string }>
  roomDocuments: Array<{ id: number; name: string; mimeType: string; status: string }>
  companyFileIds: Array<{ id: number; folderId: number | null }>
  companyFolderIds: number[]
  companySourceItems: Array<{ id: number; name: string; type: 'file' | 'folder' }>
}

interface SlotProps {
  handleSubmit: () => void
  isSubmitting: boolean
  isValid: boolean
}

interface RoomFormProps {
  view?: 'admin' | 'user'
  defaultValues?: Partial<RoomFormValues>
  disabledField?: Set<string>
  onSubmit: (values: RoomFormValues) => void | Promise<void>
  submitError?: string
  children: (slot: SlotProps) => ReactNode
  footerChildren?: (slot: SlotProps) => ReactNode
}

interface RoomDataSourcesProps {
  uploadDocuments: RoomFormValues['uploadDocuments']
  roomDocuments: RoomFormValues['roomDocuments']
  companyFileIds: RoomFormValues['companyFileIds']
  companyFolderIds: number[]
  companySourceItems: RoomFormValues['companySourceItems']
  disabledField?: Set<string>
  onUpload: (files: File[]) => void
  onRemove: (type: 'room' | 'upload', index: number) => void
  onCompanySave: (
    folders: number[],
    files: RoomFormValues['companyFileIds'],
    items: RoomFormValues['companySourceItems'],
  ) => void
}

interface SourceCardProps {
  icon: ReactNode
  title: string
  description: string
  action: ReactNode
  badges: ReactNode
  children?: ReactNode
}

interface RoomResponseSettingsProps {
  internalOnly: boolean
  useCompanyFiles: boolean
  disabled: boolean
  onChange: (internalOnly: boolean, useCompanyFiles: boolean) => void
}

// ---------- Schema ----------

const roomFormSchema = z
  .object({
    name: z.string().min(1, 'Please enter a room name.'),
    description: z.string(),
    customPrompt: z.string(),
    modelVersionId: z.string(),
    agentId: z.string(),
    departmentId: z.string(),
    allowAnyUsers: z.boolean(),
    isAnswerInternalDataOnly: z.boolean(),
    useCompanyFiles: z.boolean(),
    memberIds: z.array(z.string()),
    uploadDocuments: z.array(z.object({ name: z.string(), file: z.any(), fileType: z.string() })),
    roomDocuments: z.array(
      z.object({
        id: z.number(),
        name: z.string(),
        mimeType: z.string(),
        status: z.string(),
      }),
    ),
    companyFileIds: z.array(z.object({ id: z.number(), folderId: z.number().nullable() })),
    companyFolderIds: z.array(z.number()),
    companySourceItems: z.array(z.object({ id: z.number(), name: z.string(), type: z.enum(['file', 'folder']) })),
  })
  .refine((values) => Boolean(values.modelVersionId) !== Boolean(values.agentId), {
    message: 'Please select a model or agent.',
    path: ['modelVersionId'],
  })

// ---------- Constants ----------

const DEFAULT_VALUES: RoomFormValues = {
  name: '',
  description: '',
  customPrompt: '',
  modelVersionId: '',
  agentId: '',
  departmentId: '',
  allowAnyUsers: false,
  isAnswerInternalDataOnly: false,
  useCompanyFiles: false,
  memberIds: [],
  uploadDocuments: [],
  roomDocuments: [],
  companyFileIds: [],
  companyFolderIds: [],
  companySourceItems: [],
}

const RESPONSE_MODES = [
  { key: 'roomAndModel', internalOnly: false, useCompanyFiles: false },
  { key: 'roomOnly', internalOnly: true, useCompanyFiles: false },
  { key: 'companyAndModel', internalOnly: false, useCompanyFiles: true },
  { key: 'companyOnly', internalOnly: true, useCompanyFiles: true },
] as const

// ---------- Sub-components ----------

function SourceCard({ icon, title, description, action, badges, children }: SourceCardProps) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-200 border border-main-border-lv01 p-3">
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2 sm:flex">
        <div className="shrink-0 py-1 text-main-icon-lv01">{icon}</div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h4 className="text-ui-title-t2 leading-ui-title-t2 font-semibold">{title}</h4>
          <p className="text-ui-body-b2 leading-ui-body-b2 font-medium text-main-text-lv02">{description}</p>
          <div className="flex flex-wrap gap-1">{badges}</div>
        </div>
        <div className="col-start-2 shrink-0">{action}</div>
      </div>
      {children}
    </div>
  )
}

function CompanySourceItem({
  item,
  actions,
}: {
  item: RoomFormValues['companySourceItems'][number]
  actions: FileUploadItemProps['actions']
}) {
  const t = useTranslations('room.form')
  const isFolder = item.type === 'folder'
  const { data, isError } = useDatamanagementItems(isFolder ? { folder: item.id, page: 1, page_size: 1 } : undefined)
  const Icon = isFolder ? FolderIcon : FileDocumentIcon

  return (
    <FileUploadItem
      fileName={item.name}
      fileDescription={isFolder ? (data ? t('folderFileCount', { count: data.total }) : isError ? '—' : '…') : ''}
      icon={<Icon className="size-8 shrink-0 text-main-icon-display" aria-hidden="true" />}
      actions={actions}
    />
  )
}

function RoomDataSources({
  uploadDocuments,
  roomDocuments,
  companyFileIds,
  companyFolderIds,
  companySourceItems,
  disabledField,
  onUpload,
  onRemove,
  onCompanySave,
}: RoomDataSourcesProps) {
  const t = useTranslations('room.form')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [error, setError] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const uploadCount = uploadDocuments.length + roomDocuments.length
  const sourceLimitReached = uploadCount + companyFileIds.length + companyFolderIds.length >= 5

  function removeFile(type: 'room' | 'upload', index: number) {
    setError('')
    onRemove(type, index)
  }

  function selectFiles(files: File[]) {
    if (uploadCount + companyFileIds.length + companyFolderIds.length + files.length > 5) {
      setError(t('sourceLimitError'))
      return
    }
    if (files.some((file) => file.size > 5 * 1024 * 1024 || !/\.(txt|docx|csv|xlsx|pdf)$/i.test(file.name))) {
      setError(t('uploadFileError'))
      return
    }
    setError('')
    onUpload(files)
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h3 className="text-ui-title-t1 leading-ui-title-t1 font-semibold">{t('dataSources')}</h3>
        <p className="text-ui-body-b2 leading-ui-body-b2 font-medium text-main-text-lv02">{t('sourceLimitHint')}</p>
      </div>
      {(sourceLimitReached || error) && (
        <Alert
          variant="error"
          icon={<ReportProblemIcon className="size-4 text-main-icon-error" />}
          className="[&>span]:text-ui-body-b2 [&>span]:leading-ui-body-b2 [&>span]:font-medium"
        >
          {sourceLimitReached ? t('sourceLimitReached') : error}
        </Alert>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".txt,.docx,.csv,.xlsx,.pdf"
        className="hidden"
        aria-label={t('uploadFiles')}
        disabled={disabledField?.has('uploadDocuments') || sourceLimitReached}
        onChange={(event) => {
          selectFiles(Array.from(event.target.files ?? []))
          event.target.value = ''
        }}
      />
      <SourceCard
        icon={<UploadIcon className="size-6 text-main-icon-lv01" />}
        title={t('uploadFromDevice')}
        description={t('uploadRequirements')}
        action={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabledField?.has('uploadDocuments') || sourceLimitReached}
            onClick={() => inputRef.current?.click()}
          >
            <span>{t('uploadFiles')}</span>
          </Button>
        }
        badges={
          <Badge
            color="blue"
            leadingIcon={<FileDocumentIcon className="size-4 text-main-support-on-surface-on-blue" />}
            label={t('fileCount', { count: uploadCount })}
          />
        }
      >
        {roomDocuments.map((file, index) => (
          <FileUploadItem
            key={`room-${file.id}`}
            fileName={file.name}
            fileDescription={file.mimeType}
            isFail={file.status === 'error'}
            actions={disabledField?.has('uploadDocuments') ? [] : [{ action: () => removeFile('room', index) }]}
          />
        ))}
        {uploadDocuments.map((file, index) => (
          <FileUploadItem
            key={`upload-${index}`}
            fileName={file.name}
            fileDescription={formatFileSize(file.file.size)}
            actions={disabledField?.has('uploadDocuments') ? [] : [{ action: () => removeFile('upload', index) }]}
          />
        ))}
      </SourceCard>
      <SourceCard
        icon={<FileDocumentIcon className="size-6 text-main-icon-lv01" />}
        title={t('selectFromSystem')}
        description={t('systemKnowledgeDescription')}
        action={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabledField?.has('companyFiles') || sourceLimitReached}
            onClick={() => setPickerOpen(true)}
          >
            <span>{t('selectFilesFolders')}</span>
          </Button>
        }
        badges={
          <>
            <Badge
              color="blue"
              leadingIcon={<FolderIcon className="size-4 text-main-support-on-surface-on-blue" />}
              label={t('folderCount', { count: companyFolderIds.length })}
            />
            <Badge
              color="blue"
              leadingIcon={<FileDocumentIcon className="size-4 text-main-support-on-surface-on-blue" />}
              label={t('fileCount', { count: companyFileIds.length })}
            />
          </>
        }
      >
        {companySourceItems.map((item) => (
          <CompanySourceItem
            key={`${item.type}-${item.id}`}
            item={item}
            actions={
              disabledField?.has('companyFiles')
                ? []
                : [
                    {
                      action: () => {
                        setError('')
                        onCompanySave(
                          item.type === 'folder' ? companyFolderIds.filter((id) => id !== item.id) : companyFolderIds,
                          item.type === 'file' ? companyFileIds.filter(({ id }) => id !== item.id) : companyFileIds,
                          companySourceItems.filter((source) => source.id !== item.id || source.type !== item.type),
                        )
                      },
                    },
                  ]
            }
          />
        ))}
      </SourceCard>
      {pickerOpen && (
        <CompanyFilesPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          folderIds={companyFolderIds}
          fileIds={companyFileIds}
          sourceItems={companySourceItems}
          onSave={(folders, files, items) => {
            if (uploadCount + folders.length + files.length > 5) {
              setError(t('sourceLimitError'))
              return
            }
            setError('')
            onCompanySave(folders, files, items)
          }}
        />
      )}
    </section>
  )
}

function RoomResponseSettings({ internalOnly, useCompanyFiles, disabled, onChange }: RoomResponseSettingsProps) {
  const t = useTranslations('room.form')

  return (
    <fieldset className="flex min-w-0 flex-col gap-2" disabled={disabled}>
      <legend className="mb-2 text-ui-title-t1 leading-ui-title-t1 font-semibold">{t('responseSources')}</legend>
      <RadioGroup
        name="room-response-source"
        aria-label={t('responseSources')}
        disabled={disabled}
        value={
          RESPONSE_MODES.find((mode) => mode.internalOnly === internalOnly && mode.useCompanyFiles === useCompanyFiles)
            ?.key
        }
        onValueChange={(value) => {
          const mode = RESPONSE_MODES.find((mode) => mode.key === value)
          if (mode) onChange(mode.internalOnly, mode.useCompanyFiles)
        }}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        {RESPONSE_MODES.map((mode) => (
          <label
            key={mode.key}
            className="relative flex min-w-0 cursor-pointer items-start gap-2 rounded-300 border border-main-border-lv01 p-4 has-disabled:cursor-not-allowed has-disabled:opacity-50"
          >
            <Radio value={mode.key} className="mt-0.5" />
            <span className="flex min-w-0 flex-col gap-1">
              <span className="text-ui-title-t3 leading-ui-title-t3 font-semibold">{t(mode.key)}</span>
              <span className="text-ui-caption-c2 leading-ui-caption-c2 text-main-text-lv02">
                {t(`${mode.key}Description`)}
              </span>
            </span>
          </label>
        ))}
      </RadioGroup>
    </fieldset>
  )
}

// ---------- Component ----------

export default function RoomForm({
  view = 'admin',
  defaultValues,
  disabledField,
  onSubmit,
  submitError,
  children,
  footerChildren,
}: RoomFormProps) {
  const t = useTranslations('room.form')
  const { data: modelsData } = useCompanyModels()
  const { data: agentsData } = useCompanyAgents()
  const { data: departmentsData } = useDepartments({ page: 1, page_size: 100 })
  const { data: usersData } = useUsers({ page: 1, page_size: 1000 })

  const modelItems =
    modelsData?.map((model) => ({
      value: String(model.version_id),
      label: model.version_display_name || model.label,
    })) ?? []

  const targetItems = [
    ...modelItems.map((model) => ({ ...model, value: `model:${model.value}` })),
    ...(agentsData ?? []).map((agent) => ({
      value: `agent:${agent.id}`,
      label: agent.name,
    })),
  ]

  const departmentItems =
    departmentsData?.items.map((d) => ({
      value: String(d.id),
      label: d.name,
    })) ?? []

  const form = useForm({
    defaultValues: { ...DEFAULT_VALUES, ...defaultValues },
    validators: { onChange: roomFormSchema },
    onSubmit: async ({ value }) => {
      if (value.departmentId) {
        const validIds = new Set(userItemsRef.current.map((u) => u.value))
        value.memberIds = value.memberIds.filter((id) => validIds.has(id))
      }
      await onSubmit(value)
    },
  })

  const isValid = useStore(form.store, (s) => roomFormSchema.safeParse(s.values).success)
  const errorRef = useRef<HTMLDivElement>(null)
  const isSubmitting = useStore(form.store, (s) => s.isSubmitting)
  const allowAnyUsers = useStore(form.store, (s) => s.values.allowAnyUsers)
  const departmentId = useStore(form.store, (s) => s.values.departmentId)
  const companyFileIds = useStore(form.store, (s) => s.values.companyFileIds)
  const companyFolderIds = useStore(form.store, (s) => s.values.companyFolderIds)
  const companySourceItems = useStore(form.store, (s) => s.values.companySourceItems)
  const uploadDocuments = useStore(form.store, (s) => s.values.uploadDocuments)
  const roomDocuments = useStore(form.store, (s) => s.values.roomDocuments)
  const internalOnly = useStore(form.store, (s) => s.values.isAnswerInternalDataOnly)
  const useCompanyFiles = useStore(form.store, (s) => s.values.useCompanyFiles)

  const userItems = useMemo((): UserMultiSelectItem[] => {
    const users = usersData?.items ?? []
    const filtered = departmentId ? users.filter((u) => String(u.department?.id) === departmentId) : users
    return filtered.map((u) => ({
      value: String(u.id),
      optionLabel: `${u.first_name} ${u.last_name}`.trim() || u.username,
      optionSubLabel: u.email,
    }))
  }, [usersData, departmentId])

  const userItemsRef = useRef(userItems)
  userItemsRef.current = userItems

  const disabledSet = useMemo(() => new Set(disabledField), [disabledField])

  useEffect(() => {
    if (defaultValues) {
      form.reset({ ...DEFAULT_VALUES, ...defaultValues })
    }
  }, [form, defaultValues])

  useEffect(() => {
    if (submitError) errorRef.current?.scrollIntoView({ block: 'nearest' })
  }, [submitError])

  const descriptionField = (
    <form.Field name="description">
      {(field) => (
        <Textarea
          placeholder={t('descriptionPlaceholder')}
          floatingLabel={t('description')}
          aria-label={t('description')}
          className="block h-30 min-h-30 px-4"
          disabled={disabledSet.has('description')}
          value={field.state.value}
          onBlur={field.handleBlur}
          onChange={(e) => field.handleChange(e.target.value)}
        />
      )}
    </form.Field>
  )

  return (
    <div className={cn('flex flex-col', view === 'user' ? 'min-h-0 flex-1' : 'gap-6 mt-2')}>
      {children({
        handleSubmit: () => form.handleSubmit(),
        isSubmitting,
        isValid,
      })}

      <div
        className={
          view === 'user'
            ? 'px-6 py-8 flex min-h-0 flex-1 flex-col overflow-hidden'
            : 'h-full w-full max-w-200 px-6 py-8'
        }
      >
        <div
          className={cn(
            view === 'user' && 'min-h-0 flex-1 overflow-y-auto overscroll-contain py-2 scrollbar-gutter-stable',
          )}
        >
          <div className="flex flex-col gap-8 pr-2 text-main-text-lv01">
            {submitError && (
              <Alert
                ref={errorRef}
                variant="error"
                icon={<ReportProblemIcon className="text-main-icon-error" />}
                className="-mb-2 [&>span]:text-ui-body-b2 [&>span]:leading-ui-body-b2 [&>span]:font-medium"
              >
                {submitError}
              </Alert>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault()
                form.handleSubmit()
              }}
            >
              <FieldGroup className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
                <h3 className="col-span-full text-ui-title-t1 leading-ui-title-t1 font-semibold text-main-text-lv01">
                  {t('roomInformation')}
                </h3>
                <form.Field name="name">
                  {(field) => {
                    const errorMessage = field.state.meta.errors.map(getErrorMessage).filter(Boolean).join(', ')
                    const isInvalid = field.state.meta.isTouched && !!errorMessage
                    return (
                      <Field data-invalid={isInvalid}>
                        <Input
                          placeholder={t('namePlaceholder')}
                          floatingLabel={t('name')}
                          aria-label={t('name')}
                          required
                          disabled={disabledSet.has('name')}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          aria-invalid={isInvalid}
                          hint={isInvalid ? errorMessage : undefined}
                        />
                      </Field>
                    )
                  }}
                </form.Field>

                <form.Field name="modelVersionId">
                  {(field) => {
                    const errorMessage = field.state.meta.errors.map(getErrorMessage).filter(Boolean).join(', ')
                    const isInvalid = field.state.meta.isTouched && !!errorMessage
                    return (
                      <SimpleSelect
                        placeholder={t('modelPlaceholder')}
                        floatingLabel={t('model')}
                        aria-label={t('model')}
                        required
                        items={targetItems}
                        disabled={disabledSet.has('modelVersionId')}
                        value={
                          field.state.value
                            ? `model:${field.state.value}`
                            : form.state.values.agentId
                              ? `agent:${form.state.values.agentId}`
                              : ''
                        }
                        onValueChange={(target) => {
                          const [type, id] = target.split(':')
                          field.handleChange(type === 'model' ? id : '')
                          form.setFieldValue('agentId', type === 'agent' ? id : '')
                        }}
                        aria-invalid={isInvalid}
                        hint={isInvalid ? errorMessage : undefined}
                      />
                    )
                  }}
                </form.Field>

                <div className="col-span-full mt-2">{descriptionField}</div>

                <div className="col-span-full mt-2">
                  <form.Field name="customPrompt">
                    {(field) => (
                      <Textarea
                        placeholder={t('customPromptPlaceholder')}
                        floatingLabel={t('customPrompt')}
                        aria-label={t('customPrompt')}
                        className="block h-30 min-h-30 px-4"
                        disabled={disabledSet.has('customPrompt')}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                    )}
                  </form.Field>
                </div>

                {view === 'admin' && (
                  <div className="col-span-full mt-2 flex flex-col gap-2">
                    <form.Field name="departmentId">
                      {(field) => (
                        <SimpleSelect
                          placeholder="Department"
                          items={departmentItems}
                          disabled={disabledSet.has('departmentId')}
                          value={field.state.value}
                          onValueChange={field.handleChange}
                        />
                      )}
                    </form.Field>

                    <form.Field name="allowAnyUsers">
                      {(field) => (
                        <label
                          className={`flex items-center gap-3 py-2 ${disabledSet.has('allowAnyUsers') ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                        >
                          <Checkbox
                            checked={field.state.value}
                            onCheckedChange={(checked) => field.handleChange(checked === true)}
                            disabled={disabledSet.has('allowAnyUsers')}
                          />
                          <span className="text-ui-body-b2 leading-ui-body-b2 text-main-text-lv01">
                            Allow any users in department to use this room
                          </span>
                        </label>
                      )}
                    </form.Field>
                    {!allowAnyUsers && (
                      <form.Field name="memberIds">
                        {(field) => (
                          <UserMultiSelect
                            placeholder="Members"
                            items={userItems}
                            disabled={disabledSet.has('memberIds')}
                            value={field.state.value}
                            setValue={field.handleChange}
                            searchPlaceholder="Search users"
                            emptyMessage={
                              departmentId ? 'No users found in the selected department.' : 'No users found.'
                            }
                          />
                        )}
                      </form.Field>
                    )}
                  </div>
                )}
              </FieldGroup>
            </form>

            <RoomDataSources
              uploadDocuments={uploadDocuments}
              roomDocuments={roomDocuments}
              companyFileIds={companyFileIds}
              companyFolderIds={companyFolderIds}
              companySourceItems={companySourceItems}
              disabledField={disabledSet}
              onUpload={(files) =>
                form.setFieldValue('uploadDocuments', (previous) => [
                  ...previous,
                  ...files.map((file) => ({ name: file.name, file, fileType: 'general' })),
                ])
              }
              onRemove={(type, index) => {
                if (type === 'room') {
                  form.setFieldValue('roomDocuments', (files) => files.filter((_, i) => i !== index))
                } else {
                  form.setFieldValue('uploadDocuments', (files) => files.filter((_, i) => i !== index))
                }
              }}
              onCompanySave={(folders, files, items) => {
                form.setFieldValue('companyFolderIds', folders)
                form.setFieldValue('companyFileIds', files)
                form.setFieldValue('companySourceItems', items)
              }}
            />

            <RoomResponseSettings
              internalOnly={internalOnly}
              useCompanyFiles={useCompanyFiles}
              disabled={disabledSet.has('isAnswerInternalDataOnly') || disabledSet.has('useCompanyFiles')}
              onChange={(internalOnly, useCompanyFiles) => {
                form.setFieldValue('isAnswerInternalDataOnly', internalOnly)
                form.setFieldValue('useCompanyFiles', useCompanyFiles)
              }}
            />
          </div>
        </div>
      </div>

      {footerChildren &&
        footerChildren({
          handleSubmit: () => form.handleSubmit(),
          isSubmitting,
          isValid,
        })}
    </div>
  )
}
